import { headerViewApi } from 'app/services/api';
import { normalize, schema } from 'normalizr';

import { HeaderViewType } from 'constants/loanApplication';
import { catchAsyncActionError } from 'utils/action';
import { createNamedActions } from 'utils/namedActionTypesProxy';

import { showMessageAction } from 'store/global/actions';

export const LOAN_HEADER_VIEW_ACTION_TYPE = createNamedActions('loanHeaderViews', {
  GET_LOAN_HEADER_VIEW: 'GET_LOAN_HEADER_VIEW',
  GET_RESOLVED_CONTACT_HEADER_VIEW: 'GET_RESOLVED_CONTACT_HEADER_VIEW',
  GET_RESOLVED_LEAD_HEADER_VIEW: 'GET_RESOLVED_LEAD_HEADER_VIEW',
  GET_RESOLVED_SUB_COMPANY_HEADER_VIEW: 'GET_RESOLVED_SUB_COMPANY_HEADER_VIEW',
  GET_LOAN_HEADER_VIEW_BY_USER: 'GET_LOAN_HEADER_VIEW_BY_USER',
  GET_LOAN_HEADER_VIEW_BY_ROLE: 'GET_LOAN_HEADER_VIEW_BY_ROLE',
  GET_COMPANY_DEFAULT_HEADER_VIEWS: 'GET_COMPANY_DEFAULT_HEADER_VIEWS',
  CREATE_LOAN_HEADER_VIEW: 'CREATE_LOAN_HEADER_VIEW',
  EDIT_LOAN_HEADER_VIEW: 'EDIT_LOAN_HEADER_VIEW',
  DELETE_LOAN_HEADER_VIEW: 'DELETE_LOAN_HEADER_VIEW',
  HEADER_VIEW_USER_SET_ACTION: 'LOAN_HEADER_VIEW_USER_SET_ACTION',
  HEADER_VIEW_USER_RESET_ACTION: 'LOAN_HEADER_VIEW_USER_RESET_ACTION',
  HEADER_VIEW_ROLE_SET_ACTION: 'LOAN_HEADER_VIEW_ROLE_SET_ACTION',
  HEADER_VIEW_ROLE_RESET_ACTION: 'LOAN_HEADER_VIEW_ROLE_RESET_ACTION',
  HEADER_VIEW_COMPANY_SET_ACTION: 'LOAN_HEADER_VIEW_COMPANY_SET_ACTION',
  HEADER_VIEW_COMPANY_RESET_ACTION: 'LOAN_HEADER_VIEW_COMPANY_RESET_ACTION',
});

const loanHeaderViewSchema = new schema.Entity('data', {}, { idAttribute: 'id' });

export const getLoanHeaderViewAction = catchAsyncActionError(() => async dispatch => {
  const { data } = await headerViewApi.getView();

  return dispatch({
    type: LOAN_HEADER_VIEW_ACTION_TYPE.GET_LOAN_HEADER_VIEW,
    payload: normalize(data, [loanHeaderViewSchema]).entities.data,
  });
});

export const getResolvedContactHeaderViewAction = catchAsyncActionError(() => async dispatch => {
  const { data } = await headerViewApi.getResolvedContactHeaderView();

  return dispatch({
    type: LOAN_HEADER_VIEW_ACTION_TYPE.GET_RESOLVED_CONTACT_HEADER_VIEW,
    payload: data,
  });
});

export const getResolvedLeadHeaderViewAction = catchAsyncActionError(
  ({ userId }) =>
    async dispatch => {
      const { data } = await headerViewApi.getResolvedLeadHeaderView({
        userId,
        type: HeaderViewType.LEAD,
      });

      return dispatch({
        type: LOAN_HEADER_VIEW_ACTION_TYPE.GET_RESOLVED_LEAD_HEADER_VIEW,
        payload: data,
      });
    },
);

// The SubCompany header, like the Contact header, is resolved for the CURRENT user by the backend
// via /LoanHeaderView/resolve — the FULL user -> role -> default resolver (GetLoanHeaderViewForUser).
// It honours the HeaderViewAssignments role bindings and falls back to the company default/unbound
// SubCompany view. NOT user/{id}/get (the exact per-user PIN, which returns 204 for role-bound users)
// and NOT name-sorting the raw GET /LoanHeaderView list (which ignored role bindings entirely). DEV-4941.
export const getResolvedSubCompanyHeaderViewAction = catchAsyncActionError(() => async dispatch => {
  const { data } = await headerViewApi.getResolvedHeaderView({
    type: HeaderViewType.SUB_COMPANY,
  });

  return dispatch({
    type: LOAN_HEADER_VIEW_ACTION_TYPE.GET_RESOLVED_SUB_COMPANY_HEADER_VIEW,
    payload: data,
  });
});

export const getLoanHeaderViewByUserIdAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await headerViewApi.getViewUserGetByUserId(params);

  return dispatch({
    type: LOAN_HEADER_VIEW_ACTION_TYPE.GET_LOAN_HEADER_VIEW_BY_USER,
    payload: data,
  });
});

export const getLoanHeaderViewByRoleIdAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await headerViewApi.getViewRoleGetByRoleId(params);

  return dispatch({
    type: LOAN_HEADER_VIEW_ACTION_TYPE.GET_LOAN_HEADER_VIEW_BY_ROLE,
    payload: data,
  });
});

export const getCompanyDefaultHeaderViewsAction = catchAsyncActionError(() => async dispatch => {
  const { data } = await headerViewApi.getCompanyDefaults();

  return dispatch({
    type: LOAN_HEADER_VIEW_ACTION_TYPE.GET_COMPANY_DEFAULT_HEADER_VIEWS,
    payload: data,
  });
});

export const createLoanHeaderViewCompanySetAction = catchAsyncActionError(
  params => async dispatch => {
    const { data } = await headerViewApi.createViewCompanySet(params);

    return dispatch({
      type: LOAN_HEADER_VIEW_ACTION_TYPE.HEADER_VIEW_COMPANY_SET_ACTION,
      payload: data,
    });
  },
);

export const resetLoanHeaderViewCompanyAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await headerViewApi.createViewCompanyReset(params);

  return dispatch({
    type: LOAN_HEADER_VIEW_ACTION_TYPE.HEADER_VIEW_COMPANY_RESET_ACTION,
    payload: data,
  });
});

export const createLoanHeaderViewAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await headerViewApi.createView(params);

  return dispatch({
    type: LOAN_HEADER_VIEW_ACTION_TYPE.CREATE_LOAN_HEADER_VIEW,
    payload: data,
  });
});

export const createLoanHeaderViewUserSetAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await headerViewApi.createViewUserSet(params);

  return dispatch({
    type: LOAN_HEADER_VIEW_ACTION_TYPE.HEADER_VIEW_USER_SET_ACTION,
    payload: data,
  });
});

export const resetLoanHeaderViewUserAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await headerViewApi.createViewUserReset(params);

  return dispatch({
    type: LOAN_HEADER_VIEW_ACTION_TYPE.HEADER_VIEW_USER_RESET_ACTION,
    payload: data,
  });
});

export const createLoanHeaderViewRoleSetAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await headerViewApi.createViewRoleSet(params);

  return dispatch({
    type: LOAN_HEADER_VIEW_ACTION_TYPE.HEADER_VIEW_ROLE_SET_ACTION,
    payload: data,
  });
});

export const resetLoanHeaderViewRoleAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await headerViewApi.createViewRoleReset(params);

  return dispatch({
    type: LOAN_HEADER_VIEW_ACTION_TYPE.HEADER_VIEW_ROLE_RESET_ACTION,
    payload: data,
  });
});

export const deleteLoanHeaderViewAction = catchAsyncActionError(params => async dispatch => {
  await headerViewApi.deleteView(params);

  dispatch({
    type: LOAN_HEADER_VIEW_ACTION_TYPE.DELETE_LOAN_HEADER_VIEW,
    payload: params.loanHeaderViewId,
  });
  dispatch(showMessageAction({ message: 'Successfully deleted', variant: 'success' }));
});
